/*  MUI  */
import {
    Box,
    Button,
    FormControlLabel,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import { AuvikSwitch } from 'application/Shared/Components/Switches';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Route } from 'application/Shared/Helpers';

/*  Objects  */
import { Application } from 'objects/Application';
import {
    Breach,
    BreachClientsImpacted,
    BreachDescription,
    BreachResources,
    BreachScore
} from 'objects/Breach';

/*  React  */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

const headers = (t) => [
    {
        align: 'left',
        field: 'dateAnnounced',
        renderHeader: <strong>{t('Common.date')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.app_and_description')}</strong>,
        width: '40%'
    },
    {
        align: 'left',
        field: 'severityScore',
        renderHeader: <strong>{t('Common.score')}</strong>,
        width: '5%'
    },
    {
        align: 'left',
        field: 'type',
        renderHeader: <strong>{t('Common.sources')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'type',
        renderHeader: <strong>{t('Common.impact')}</strong>,
        width: '15%'
    },
    {
        align: 'right',
        field: 'type',
        renderHeader: <strong>{t('Common.actions')}</strong>,
        width: '15%'
    }
];

const RenderRow = ({ row }) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();
    const route = Route.buildBaseAdminRoute({ clientId, partnerId });

    return (
        <Breach data={row} key={row.id}>
            <TableCell>
                <DateFormat date={row.dateAnnounced} noTimeString />
            </TableCell>
            <TableCell>
                <Application
                    data={row.application}
                    fromPage={t('Common.vendor_incidents')}
                    nameLinkTo={
                        row.clientsImpacted > 0 &&
                        `${route}/application/${row.application.id}/users`
                    }
                >
                    <ApplicationNameLinkCell
                        nameClassname="bold"
                        withCategory
                    />
                </Application>
                <BreachDescription noTruncate includeSteps />
            </TableCell>
            <TableCell>
                <BreachScore />
            </TableCell>
            <TableCell>
                <BreachResources noTitle />
            </TableCell>
            <TableCell>
                <BreachClientsImpacted onlyThisTenant />
            </TableCell>
            <TableCell>
                {row.clientsImpacted > 0 && (
                    <Link
                        to={`${route}/application/${row.application.id}/users`}
                        state={{ from: t('Common.vendor_incidents') }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                        >
                            {t('Common.view_impacted_users')}
                        </Button>
                    </Link>
                )}
            </TableCell>
        </Breach>
    );
};

const searchFields = ['description'];

export const AdminVendorIncidentsTable = () => {
    const { t } = useTranslation();
    const [onlyShowImpactedClients, setOnlyShowImpactedClients] =
        useState(true);

    const parameters = useMemo(
        () => ({
            includeApplication: true,
            includeClientsImpacted: true,
            includeOnlyImpactedClients: onlyShowImpactedClients,
            includeOnlyThisTenant: true
        }),
        [onlyShowImpactedClients]
    );

    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <Box data-trackid="adminVendorIncidentsPagesPage">
            <Box mb="8px" mt="8px">
                <FormControlLabel
                    control={
                        <Box>
                            <AuvikSwitch
                                checked={onlyShowImpactedClients}
                                onChange={() =>
                                    setOnlyShowImpactedClients(
                                        !onlyShowImpactedClients
                                    )
                                }
                                inputProps={{
                                    'aria-label': 'controlled'
                                }}
                            />
                        </Box>
                    }
                    label={
                        <Typography variant="p" style={{ marginLeft: '8px' }}>
                            {t('VendorIncident.onlyImpactedClients')}
                        </Typography>
                    }
                    style={{ marginLeft: 0 }}
                />
            </Box>
            <ManagedTable
                emptyRowsMessage={t('VendorIncident.noImpactedClients')}
                headers={tableHeaders}
                parameters={parameters}
                renderBody={(row) => <RenderRow row={row} />}
                renderBodyKey="id"
                renderFooter
                rowsApi="/breach"
                rowsApiPreload
                rowsPerPage={5}
                searchEnabled
                searchFields={searchFields}
                sortDirection="DESC"
                sortFieldType="date"
                sortKey="dateAnnounced"
            />
        </Box>
    );
};
